/**
 * CollectionBrowserPage - Museum-style collection browser
 * 
 * REFACTORED: Now uses shared PublicLayout (Header + Footer) from main site.
 * 
 * Features:
 * - Shared public header with full navigation
 * - Grid and list view modes
 * - Search and filtering by category, status
 * - Collection groups
 * - Links to individual artifact detail pages
 * - Live data from /api/collection-items/public API
 * - Compact scholarly empty state
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Grid, List, Filter, ChevronRight, X, Package, Tag, DollarSign, SlidersHorizontal, Home, ArrowLeft, ExternalLink } from 'lucide-react';
import { Header, Footer } from '../../layouts/PublicLayout';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'ticket', label: 'Tickets' },
  { value: 'design_mockup', label: 'Design Mock-ups' },
  { value: 'production_material', label: 'Production Materials' },
  { value: 'document', label: 'Documents' },
  { value: 'photograph', label: 'Photographs' },
  { value: 'memorabilia', label: 'Memorabilia' },
  { value: 'costume', label: 'Costumes' },
  { value: 'award', label: 'Awards' },
  { value: 'certification', label: 'Certifications' },
  { value: 'other', label: 'Other' }
];

const RELATIONSHIPS = [
  { value: '', label: 'All Items' },
  { value: 'aloha_direct', label: 'Aloha from Hawaii' },
  { value: 'aloha_related', label: 'Aloha Related' },
  { value: 'elvis_general', label: 'Elvis Presley' },
  { value: 'historical_context', label: 'Historical Context' }
];

const AVAILABILITY_OPTIONS = [
  { value: '', label: 'Any Availability' },
  { value: 'available_private_sale', label: 'Available for Sale' },
  { value: 'price_on_request', label: 'Price on Request' },
  { value: 'open_to_offers', label: 'Open to Offers' },
  { value: 'not_currently_available', label: 'Not Available' },
  { value: 'permanent_collection', label: 'Permanent Collection' },
  { value: 'sold', label: 'Sold' }
];

const SORT_OPTIONS = [
  { value: 'created_at:desc', label: 'Newest First' },
  { value: 'created_at:asc', label: 'Oldest First' },
  { value: 'title:asc', label: 'Title A-Z' },
  { value: 'title:desc', label: 'Title Z-A' },
  { value: 'appraised_value_usd:desc', label: 'Highest Value' },
  { value: 'appraised_value_usd:asc', label: 'Lowest Value' }
];

const CATEGORY_LABELS = {
  ticket: 'Ticket',
  design_mockup: 'Mock-up',
  production_material: 'Production',
  document: 'Document',
  photograph: 'Photo',
  memorabilia: 'Memorabilia',
  costume: 'Costume',
  award: 'Award',
  certification: 'Certification',
  other: 'Other'
};

const STATUS_DISPLAY = {
  available_private_sale: { label: 'Available', color: 'bg-green-100 text-green-700' },
  price_on_request: { label: 'Price on Request', color: 'bg-blue-100 text-blue-700' },
  open_to_offers: { label: 'Inquiries Invited', color: 'bg-purple-100 text-purple-700' },
  not_currently_available: { label: 'Not Available', color: 'bg-gray-100 text-gray-600' },
  permanent_collection: { label: 'Permanent Collection', color: 'bg-sky-100 text-sky-700' },
  sold: { label: 'Sold', color: 'bg-emerald-100 text-emerald-700' }
};

// ============================================================================
// ARTIFACT CARD COMPONENT
// ============================================================================

const ArtifactCard = ({ artifact, viewMode }) => {
  const navigate = useNavigate();
  
  const primaryImage = artifact.primary_image || artifact.images?.[0];
  const imageSrc = primaryImage?.file_path ? `${API_BASE}${primaryImage.file_path}` : null;
  const statusConfig = STATUS_DISPLAY[artifact.availability_status] || STATUS_DISPLAY.not_currently_available;
  
  const formatPrice = (value) => {
    if (!value) return null;
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };
  
  if (viewMode === 'list') {
    return (
      <Link
        to={`/collection/items/${artifact.slug}`}
        className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-400 hover:shadow-md transition-all group"
        style={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)' }}
        data-testid={`artifact-card-${artifact.id}`}
      >
        {/* Thumbnail */}
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
          {imageSrc ? (
            <img src={imageSrc} alt={artifact.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={28} className="text-gray-300" />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 group-hover:text-[#0b2545] transition-colors line-clamp-1">
              {artifact.title}
            </h3>
            <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>
          
          <p className="text-xs text-gray-500 mb-2">{artifact.inventory_number}</p>
          
          <div className="flex items-center gap-3 text-sm">
            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
              {CATEGORY_LABELS[artifact.category] || artifact.category}
            </span>
            {artifact.show_appraised_value && artifact.appraised_value_usd && (
              <span className="text-gray-700 font-medium">
                {formatPrice(artifact.appraised_value_usd)}
              </span>
            )}
          </div>
        </div>
        
        <ChevronRight size={20} className="text-gray-300 group-hover:text-[#c9a45c] transition-colors flex-shrink-0" />
      </Link>
    );
  }
  
  // Grid view
  return (
    <Link
      to={`/collection/items/${artifact.slug}`}
      className="artifact-card bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 hover:shadow-lg transition-all group"
      style={{ 
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        scrollMarginBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))'
      }}
      data-testid={`artifact-card-${artifact.id}`}
    >
      {/* Image - reduced height on mobile */}
      <div className="artifact-card__media bg-gray-100 relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
        {imageSrc ? (
          <img 
            src={imageSrc} 
            alt={artifact.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={48} className="text-gray-300" />
          </div>
        )}
        
        {/* Status badge */}
        <span className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-medium ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
      </div>
      
      {/* Content - reduced padding on mobile */}
      <div className="artifact-card__content p-4">
        <span className="text-xs text-gray-500 block mb-1">{artifact.inventory_number}</span>
        <h3 className="font-semibold text-gray-900 group-hover:text-[#0b2545] transition-colors line-clamp-2 mb-2 min-h-[2.5rem]">
          {artifact.title}
        </h3>
        
        <div className="flex items-center justify-between">
          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
            {CATEGORY_LABELS[artifact.category] || artifact.category}
          </span>
          {artifact.show_appraised_value && artifact.appraised_value_usd && (
            <span className="text-sm text-gray-800 font-semibold">
              {formatPrice(artifact.appraised_value_usd)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// ============================================================================
// COLLECTION GROUPS SECTION
// ============================================================================

const CollectionGroupCard = ({ group, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col p-6 bg-white border border-gray-200 rounded-xl text-left hover:border-gray-400 hover:shadow-md transition-all group"
    style={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)' }}
  >
    <span className="text-3xl mb-4">{group.icon}</span>
    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#0b2545] mb-1">
      {group.name}
    </h3>
    {group.description && (
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{group.description}</p>
    )}
    <div className="mt-auto flex items-center text-sm text-[#9f7d3e]">
      <span>Browse {group.artifact_count || 0} items</span>
      <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
    </div>
  </button>
);

// ============================================================================
// EMPTY STATE COMPONENT - Scholarly, Compact
// ============================================================================

const EmptyState = ({ hasActiveFilters, onClearFilters }) => (
  <div 
    style={{
      maxWidth: '600px',
      margin: '32px auto',
      padding: '32px',
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      textAlign: 'center'
    }}
    data-testid="collection-empty-state"
  >
    <Package size={40} style={{ margin: '0 auto 16px', color: '#9CA3AF' }} />
    <h3 style={{
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '20px',
      fontWeight: 600,
      color: '#0D273C',
      marginBottom: '12px'
    }}>
      No Published Collection Artifacts Available
    </h3>
    <p style={{
      fontFamily: "'Source Sans 3', sans-serif",
      fontSize: '14px',
      lineHeight: 1.6,
      color: '#6B7280',
      marginBottom: '24px',
      maxWidth: '480px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      {hasActiveFilters 
        ? 'No artifacts match your current search criteria. Try adjusting your filters or search terms.'
        : 'Public records will appear here after their catalog information, media, and publication status have been reviewed.'}
    </p>
    
    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          style={{
            padding: '12px 20px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '14px',
            fontWeight: 600,
            color: 'white',
            backgroundColor: '#D85C4A',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Clear Filters
        </button>
      )}
      <Link
        to="/ticket-registry"
        style={{
          padding: '12px 20px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          color: '#0D273C',
          backgroundColor: 'transparent',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        Explore the Ticket Registry
      </Link>
      <Link
        to="/"
        style={{
          padding: '12px 20px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          color: '#6B7280',
          backgroundColor: 'transparent',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        Return to Main Site
      </Link>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CollectionBrowserPage = () => {
  const { groupSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [artifacts, setArtifacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Filters from URL
  const search = searchParams.get('q') || '';
  const category = searchParams.get('category') || '';
  const relationship = searchParams.get('relationship') || '';
  const availability = searchParams.get('availability') || '';
  const sortBy = searchParams.get('sort') || 'created_at:desc';
  
  // Load collection groups
  useEffect(() => {
    loadGroups();
  }, []);
  
  const loadGroups = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/collection-items/public/groups/list`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  };
  
  // Load artifacts
  useEffect(() => {
    loadArtifacts();
  }, [page, search, category, relationship, availability, sortBy, groupSlug]);
  
  const loadArtifacts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [sortField, sortOrder] = sortBy.split(':');
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '24',
        sort_by: sortField,
        sort_order: sortOrder || 'desc'
      });
      
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (relationship) params.append('relationship', relationship);
      if (availability) params.append('availability_filter', availability);
      if (groupSlug) params.append('collection_group', groupSlug);
      
      const response = await fetch(`${API_BASE}/api/collection-items/public?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to load artifacts');
      }
      
      const data = await response.json();
      setArtifacts(data.artifacts || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Update filters
  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setPage(1);
    setSearchParams(newParams);
  };
  
  const clearFilters = () => {
    setSearchParams({});
    setPage(1);
  };
  
  const hasActiveFilters = search || category || relationship || availability;
  
  // Search handler with debounce
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        updateFilter('q', searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  
  // Selected group info
  const selectedGroup = groupSlug ? groups.find(g => g.slug === groupSlug) : null;
  
  return (
    <div style={{ backgroundColor: '#FFFFFF' }}>
        <Helmet>
          <title>Collection | Elvis Aloha Collection</title>
          <meta name="description" content="Browse the museum-style catalog of documented tickets, production materials, and artifacts from Elvis Presley's Aloha from Hawaii 1973 concert." />
          <link rel="canonical" href={`${window.location.origin}/collection`} />
        </Helmet>
        {/* Collection Hero Section - Compact (190-220px) */}
        <section 
          style={{ 
            backgroundColor: '#211f1e',
            padding: '24px 0 28px'
          }}
          data-testid="collection-hero"
        >
          <div style={{ width: 'min(100% - 48px, 1200px)', marginInline: 'auto' }}>
            {/* Breadcrumb - tight spacing */}
            <nav 
              aria-label="Breadcrumb"
              style={{ marginBottom: '12px' }}
            >
              <ol style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                listStyle: 'none',
                margin: 0,
                padding: 0,
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '12px'
              }}>
                <li>
                  <Link to="/" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li style={{ color: 'rgba(255,255,255,0.4)' }}>/</li>
                {groupSlug ? (
                  <>
                    <li>
                      <Link to="/collection" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>
                        Collection
                      </Link>
                    </li>
                    <li style={{ color: 'rgba(255,255,255,0.4)' }}>/</li>
                    <li style={{ color: 'rgba(255,255,255,0.86)' }}>
                      {selectedGroup?.name || 'Group'}
                    </li>
                  </>
                ) : (
                  <li style={{ color: 'rgba(255,255,255,0.86)' }}>Collection</li>
                )}
              </ol>
            </nav>
            
            {/* Two-column layout: Text left, Action right */}
            <div className="collection-hero-inner" style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              alignItems: 'end',
              gap: '32px'
            }}>
              {/* Left: Title + Description */}
              <div>
                {/* Eyebrow */}
                <p style={{
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#C9A962',
                  margin: '0 0 6px 0'
                }}>
                  THE PUBLIC COLLECTION
                </p>
                
                {/* Title */}
                <h1 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 'clamp(2rem, 3vw, 2.75rem)',
                  fontWeight: 600,
                  color: '#ffffff',
                  margin: '0 0 8px 0',
                  lineHeight: 1
                }}>
                  {groupSlug ? (selectedGroup?.name || 'Collection Group') : 'The Collection'}
                </h1>
                
                {/* Description */}
                <p style={{
                  fontFamily: "'Source Serif 4', serif",
                  fontSize: '15px',
                  lineHeight: 1.55,
                  color: 'rgba(255, 255, 255, 0.86)',
                  maxWidth: '760px',
                  margin: 0
                }}>
                  {groupSlug && selectedGroup?.description 
                    ? selectedGroup.description
                    : 'Documented Elvis Presley memorabilia, concert artifacts, production materials, and historical records.'}
                </p>
              </div>
              
              {/* Right: Actions */}
              <div className="collection-hero-actions" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '10px',
                alignItems: 'flex-end'
              }}>
                <Link
                  to="/ticket-registry"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    fontFamily: "'Source Sans 3', sans-serif",
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: '#0D273C',
                    backgroundColor: '#FFFFFF',
                    border: 'none',
                    borderRadius: '3px',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Explore Ticket Registry
                </Link>
                <Link
                  to="/"
                  style={{
                    fontFamily: "'Source Sans 3', sans-serif",
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.6)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <ArrowLeft size={12} />
                  Main Site
                </Link>
              </div>
            </div>
          </div>
          
          {/* Responsive styles for hero and mobile artifact cards */}
          <style>{`
            @media (max-width: 760px) {
              .collection-hero-inner {
                grid-template-columns: 1fr !important;
                gap: 14px !important;
              }
              .collection-hero-actions {
                flex-direction: row !important;
                align-items: center !important;
                justify-content: flex-start !important;
              }
              [data-testid="collection-hero"] {
                padding: 20px 0 22px !important;
              }
            }
            
            /* Mobile artifact card optimizations */
            @media (max-width: 600px) {
              .artifact-card__media {
                aspect-ratio: 4 / 3 !important;
                min-height: 0 !important;
              }
              .artifact-card__content {
                padding: 14px 16px 18px !important;
              }
              .artifact-card {
                scroll-margin-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
              }
              .collection-content {
                padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px) + 24px) !important;
              }
            }
          `}</style>
        </section>
        
        {/* Collection Groups (only on main collection page) */}
        {!groupSlug && groups.length > 0 && (
          <section style={{ 
            width: 'min(100% - 48px, 1200px)', 
            marginInline: 'auto', 
            paddingTop: '24px',
            paddingBottom: '16px'
          }}>
            <h2 style={{ 
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#0D273C',
              marginBottom: '16px'
            }}>
              Browse by Collection
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {groups.map(group => (
                <CollectionGroupCard 
                  key={group.id} 
                  group={group}
                  onClick={() => navigate(`/collection/groups/${group.slug}`)}
                />
              ))}
            </div>
          </section>
        )}
        
        {/* Main Content - Reduced top padding, bottom clearance for mobile nav */}
        <section 
          className="collection-content"
          style={{ 
            width: 'min(100% - 48px, 1200px)', 
            marginInline: 'auto',
            paddingTop: '24px',
            paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px) + 24px)'
          }}
        >
          {/* Toolbar - Compact */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '20px',
            alignItems: 'center'
          }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 300px', minWidth: '200px' }}>
              <Search size={18} style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#9CA3AF' 
              }} />
              <input
                type="text"
                placeholder="Search artifacts..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 36px 10px 40px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  outline: 'none'
                }}
              />
              {searchInput && (
                <button 
                  onClick={() => { setSearchInput(''); updateFilter('q', ''); }}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9CA3AF',
                    padding: '4px'
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            
            {/* Controls Row */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 14px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  color: hasActiveFilters ? '#9f7d3e' : '#0D273C',
                  backgroundColor: hasActiveFilters ? '#fef9e7' : 'white',
                  border: hasActiveFilters ? '1px solid #c9a45c' : '1px solid #D1D5DB',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <SlidersHorizontal size={16} />
                Filters
                {hasActiveFilters && (
                  <span style={{
                    width: '18px',
                    height: '18px',
                    backgroundColor: '#D85C4A',
                    color: 'white',
                    borderRadius: '50%',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {[category, relationship, availability].filter(Boolean).length}
                  </span>
                )}
              </button>
              
              {/* View Toggle */}
              <div style={{ display: 'flex', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '4px', overflow: 'hidden' }}>
                <button
                  onClick={() => setViewMode('grid')}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: viewMode === 'grid' ? '#E5E7EB' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#0D273C'
                  }}
                  aria-label="Grid view"
                >
                  <Grid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: viewMode === 'list' ? '#E5E7EB' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#0D273C'
                  }}
                  aria-label="List view"
                >
                  <List size={16} />
                </button>
              </div>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => updateFilter('sort', e.target.value)}
                style={{
                  padding: '10px 12px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '13px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Filter Panel */}
          {filtersOpen && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0D273C', marginBottom: '6px' }}>
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => updateFilter('category', e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                  >
                    {CATEGORIES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0D273C', marginBottom: '6px' }}>
                    Collection
                  </label>
                  <select
                    value={relationship}
                    onChange={(e) => updateFilter('relationship', e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                  >
                    {RELATIONSHIPS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0D273C', marginBottom: '6px' }}>
                    Availability
                  </label>
                  <select
                    value={availability}
                    onChange={(e) => updateFilter('availability', e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                  >
                    {AVAILABILITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                {hasActiveFilters && (
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      onClick={clearFilters}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        color: '#D85C4A',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Results count */}
          <p style={{
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            color: '#6B7280',
            marginBottom: '16px'
          }}>
            {loading ? 'Loading...' : `${total} artifact${total !== 1 ? 's' : ''} found`}
          </p>
          
          {/* Error state */}
          {error && (
            <div style={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              color: '#B91C1C'
            }}>
              {error}
            </div>
          )}
          
          {/* Loading state */}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #E5E7EB',
                borderTop: '3px solid #0b2545',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          
          {/* Empty state - Compact, Scholarly */}
          {!loading && artifacts.length === 0 && (
            <EmptyState hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters} />
          )}
          
          {/* Artifacts Grid/List */}
          {!loading && artifacts.length > 0 && (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
                : 'flex flex-col gap-3'
            }>
              {artifacts.map(artifact => (
                <ArtifactCard key={artifact.id} artifact={artifact} viewMode={viewMode} />
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {!loading && total > 24 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '40px' }}>
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                style={{
                  padding: '10px 18px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <span style={{
                padding: '10px 18px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '13px',
                color: '#6B7280'
              }}>
                Page {page} of {Math.ceil(total / 24)}
              </span>
              <button
                disabled={page >= Math.ceil(total / 24)}
                onClick={() => setPage(p => p + 1)}
                style={{
                  padding: '10px 18px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: page >= Math.ceil(total / 24) ? 'not-allowed' : 'pointer',
                  opacity: page >= Math.ceil(total / 24) ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          )}
        </section>
    </div>
  );
};

export default CollectionBrowserPage;
